
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useFavorites } from "@/contexts/FavoritesProvider";
import { useAuth } from "@/contexts/auth";
import {
  Heart
} from "lucide-react";

interface FavoriteButtonProps {
  providerId: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const FavoriteButton = ({ providerId, size = "md", className }: FavoriteButtonProps) => {
  const { user } = useAuth();
  const { isFavorite, toggleFavorite, isLoading } = useFavorites();

  if (!user) {
    return null; // Don't show favorite button for non-authenticated users
  }

  const handleToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await toggleFavorite(providerId);
  };

  const sizeClasses = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12"
  };

  const iconSizes = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6"
  };

  const isFav = isFavorite(providerId);

  return (
    <Button
      variant="outline"
      size="icon"
      className={cn(
        sizeClasses[size],
        "rounded-full border-2 transition-all duration-200",
        isFav
          ? "border-red-100 bg-red-50 text-coral hover:bg-red-50"
          : "border-brand-cloud bg-white/90 text-brand-slate hover:bg-brand-mist",
        className
      )}
      onClick={handleToggle}
      disabled={isLoading}
    >
      <Heart
        className={cn(
          iconSizes[size],
          "transition-all duration-200",
          isFav ? "fill-coral text-coral" : "text-brand-slate"
        )}
      />
    </Button>
  );
};

export default FavoriteButton;
